import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { EmptyCellMark } from "@/features/propertyCodeAcquisition/components/EmptyCellMark";
import { getZenbujikoOptionLabel } from "@/features/propertyCodeAcquisition/utils/zenbujikoOptionLabels";
import type { HistoryPictureCell } from "@/features/propertyCodeAcquisition/types";

interface DownloadCellProps {
  cell: HistoryPictureCell | undefined;
  /** アクセシビリティ用ラベル（例: 「全部事項をダウンロード」）。 */
  label: string;
}

/** 失敗理由が取れない（errorDetail が null/空）場合のフォールバック文言。 */
const FAILURE_FALLBACK_TEXT = "取得失敗（理由不明）";

/**
 * リンク先に設定する前に http/https のみを許可する（IPA『安全なウェブサイトの作り方』のURL出力時のスキーム検証原則）。
 * cell.uri はBE発行の署名付きURLのみを想定するが、想定外のスキーム（スクリプト実行を伴うURL等）が
 * 返っても実行されないよう多層防御として検証する。
 */
const isSafeHttpUrl = (uri: string): boolean => {
  try {
    return ["http:", "https:"].includes(new URL(uri).protocol);
  } catch {
    return false;
  }
};

/**
 * 取得時に選択された全部事項オプションの表示文（例: 「取得オプション: 共同担保目録 / 信託目録」）を作る。
 * 全部事項セルのみ値を持ち得る。オプションがなければ空文字を返す（呼び出し側で Tooltip 非表示に分岐）。
 */
const buildOptionsText = (options: HistoryPictureCell["options"]): string => {
  const labels = (options ?? []).map(getZenbujikoOptionLabel);
  return labels.length > 0 ? `取得オプション: ${labels.join(" / ")}` : "";
};

/**
 * 要素を Tooltip でラップする（title が空なら素の要素を返す）。
 * フォーカス可能な要素（活性リンク等）は Tooltip に直接渡し、フォーカスで発火させる
 * （span を挟むと無名のタブストップが増えるため挟まない）。
 * フォーカス不可の要素（非活性ボタン・アイコン）は MUI 仕様で Tooltip が発火しないため
 * span で挟み、キーボードでも Tooltip を出せるよう tabIndex とアクセシブル名を span 側に持たせる。
 */
const wrapWithTooltip = (
  node: React.ReactElement,
  title: string,
  wrapper?: { ariaLabel: string; role?: string }
): React.ReactElement => {
  if (!title) {
    return node;
  }
  const content = wrapper ? (
    <span role={wrapper.role} tabIndex={0} aria-label={wrapper.ariaLabel}>
      {node}
    </span>
  ) : (
    node
  );
  return <Tooltip title={title}>{content}</Tooltip>;
};

/**
 * 取得履歴の登記・図面セル 1 つ分の表示。
 * - 種別なし（exists=false）: ハイフン表示。
 * - 取得失敗（status=FAILED）: 失敗が分かる表示（色のみに依存しない）。
 *   Tooltip に errorDetail（失敗理由の生メッセージ）を全文表示し、取れない場合は
 *   「取得失敗（理由不明）」を出す。
 * - uri あり（http/https）: DL アイコンを活性表示し、当該種別 PDF を直接開く（単一 DL）。
 *   acquireBookStatus（GET /api/picture-request/{id}）の uri 直開き方式と揃える。
 * - uri なし（処理中/種別なし等）・スキームが http/https 以外: DL アイコンを非活性にし押下不可。
 * 全部事項セル（options あり）は全状態で「取得オプション」を Tooltip に併記する
 * （いずれの状態もアクセシブル名（aria-label）に併記する）。
 */
const DownloadCellComponent: React.FC<DownloadCellProps> = ({
  cell,
  label,
}) => {
  if (cell == null || !cell.exists) {
    return <EmptyCellMark />;
  }

  const optionsText = buildOptionsText(cell.options);

  if (cell.status === "FAILED") {
    // 失敗理由は BE（スクレイピング側）の生メッセージをそのまま表示する。
    const failureText = cell.errorDetail?.trim()
      ? cell.errorDetail
      : FAILURE_FALLBACK_TEXT;
    const tooltipText = optionsText
      ? `${failureText} / ${optionsText}`
      : failureText;
    const errorLabel = `${label}（${tooltipText}）`;
    // アイコン自体はアクセシブル名を持たないため、ラップ要素側に付与する。
    return wrapWithTooltip(
      <ErrorOutlineIcon color="error" fontSize="small" aria-hidden="true" />,
      tooltipText,
      { ariaLabel: errorLabel, role: "img" }
    );
  }

  // 取得オプションはアクセシブル名にも含める（Tooltip はマウス操作でしか見えないため）。
  const combinedLabel = optionsText ? `${label}（${optionsText}）` : label;

  // uri なし（処理中等）・スキームが http/https 以外は DL 不可。非活性アイコンを出す。
  // 空文字も弾く（空文字をリンク先にすると現在ページへ遷移＝リロードになるため）。
  if (!cell.uri || !isSafeHttpUrl(cell.uri)) {
    if (optionsText) {
      return wrapWithTooltip(
        // aria-label はラップ要素側に一本化する（同名要素が 2 つあるとテストの getByRole 特定が曖昧になる）。
        <IconButton size="small" disabled aria-hidden="true">
          <CloudDownloadOutlinedIcon fontSize="small" color="disabled" />
        </IconButton>,
        optionsText,
        { ariaLabel: combinedLabel, role: "img" }
      );
    }
    return (
      <IconButton size="small" aria-label={combinedLabel} disabled>
        <CloudDownloadOutlinedIcon fontSize="small" color="disabled" />
      </IconButton>
    );
  }

  // uri あり: href 直開き＝単一 DL（署名付き URL の Content-Disposition で DL）。
  const downloadButton = (
    <IconButton
      size="small"
      aria-label={combinedLabel}
      href={cell.uri}
      target="_blank"
      rel="noopener noreferrer"
    >
      <CloudDownloadOutlinedIcon fontSize="small" color="primary" />
    </IconButton>
  );
  return wrapWithTooltip(downloadButton, optionsText);
};

export const DownloadCell = React.memo(DownloadCellComponent);
